import { useState, useEffect, useRef, useCallback } from 'react'
import toast from 'react-hot-toast'
import { useCV } from '../hooks/useCV'
import { useLocalCVList } from '../hooks/useLocalCVList'
import { useOnboarding } from '../hooks/useOnboarding'
import { useAuth } from '../contexts/AuthContext'
import { useTranslation } from '../contexts/LanguageContext'
import { usePdfGenerator } from '../hooks/usePdfGenerator'
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts'
import { CVListPanel } from '../components/layout/CVListPanel'
import { PreviewPane } from '../components/layout/PreviewPane'
import { CVFormSidebar } from '../components/cv-form/CVFormSidebar'
import { ImportModal } from '../components/import'
import { AuthModal } from '../components/auth/AuthModal'
import { WelcomeScreen } from '../components/onboarding'
import { ShortcutsModal } from '../components/ui/ShortcutsModal'
import { DownloadModal } from '../components/download/DownloadModal'
import type { TemplateType } from '../hooks/usePdfGenerator'
import type { CV } from '../types/cv'

type MobileTab = 'edit' | 'cvs' | 'preview'

export function SinglePageApp() {
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false)
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)
  const [showShortcuts, setShowShortcuts] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [mobileTab, setMobileTab] = useState<MobileTab>('preview')
  const [template, setTemplate] = useState<TemplateType>('clean')
  const [showWelcome, setShowWelcome] = useState(false)
  const [showDownloadModal, setShowDownloadModal] = useState(false)

  const { cv, activeCVId, loadCV, undo, redo } = useCV()
  const { user } = useAuth()
  const { t } = useTranslation()
  const { downloadPdf } = usePdfGenerator()
  const { cvs, addCV, updateCV, getCV } = useLocalCVList()
  const { hasCompletedOnboarding, completeOnboarding } = useOnboarding()

  // Keyboard shortcuts
  const colorPalette = cv.settings?.colorPalette
  useKeyboardShortcuts({
    onUndo: undo,
    onRedo: redo,
    onSave: () => toast(t.editor.alreadyAutoSaved, { icon: '💾' }),
    onDownloadPdf: () => setShowDownloadModal(true),
    onShowShortcuts: () => setShowShortcuts(true),
  })

  const executeDownload = useCallback(async () => {
    await downloadPdf(cv, template, colorPalette)
  }, [downloadPdf, cv, template, colorPalette])

  // Track if this is first mount to avoid double saves
  const isInitialized = useRef(false)
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const previousCVIdRef = useRef<string | null>(null)

  // Initialize with the first CV or show welcome screen
  useEffect(() => {
    if (isInitialized.current) return
    if (hasCompletedOnboarding === null) return // Wait for localStorage read

    if (cvs.length > 0) {
      // Load the most recent CV
      const mostRecent = cvs[0]
      loadCV(mostRecent.id, mostRecent.cv)
      setTemplate(mostRecent.template)
      previousCVIdRef.current = mostRecent.id
      isInitialized.current = true
    } else if (!hasCompletedOnboarding) {
      // First-time user: show welcome screen
      setShowWelcome(true)
      isInitialized.current = true
    } else {
      // User completed onboarding before but has no CVs - show welcome screen
      setShowWelcome(true)
      isInitialized.current = true
    }
  }, [cvs.length, loadCV, addCV, template, hasCompletedOnboarding])

  // Load the template ONLY when switching to a different CV
  useEffect(() => {
    if (!activeCVId || !isInitialized.current) return

    // Only load template when we actually switch to a different CV
    if (previousCVIdRef.current === activeCVId) return

    const currentCV = getCV(activeCVId)
    if (currentCV) {
      setTemplate(currentCV.template)
      previousCVIdRef.current = activeCVId
    }
  }, [activeCVId, getCV])

  // Sync CV changes to the list (debounced)
  useEffect(() => {
    if (!activeCVId || !isInitialized.current) return

    // Check if this CV exists in the list
    const existingCV = getCV(activeCVId)
    if (!existingCV) return // Don't sync if CV not in list yet

    // Clear previous timeout
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current)
    }

    // Debounce the sync to avoid too many updates
    syncTimeoutRef.current = setTimeout(() => {
      updateCV(activeCVId, { cv, template })
    }, 1500) // 1.5 second debounce

    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current)
      }
    }
  }, [cv, activeCVId, template, getCV, updateCV])

  const handleMobileImportClick = useCallback(() => {
    if (!user && cvs.length >= 1) {
      setShowAuthModal(true)
      return
    }
    setIsImportModalOpen(true)
  }, [user, cvs.length])

  const handleImportComplete = useCallback((importedCV: CV, title: string) => {
    // Add the imported CV to the list
    const cvId = addCV(importedCV, title, template)
    // Load it into the editor
    loadCV(cvId, importedCV)
    // Mark onboarding as complete and hide welcome screen
    completeOnboarding()
    setShowWelcome(false)
  }, [addCV, loadCV, template, completeOnboarding])

  const handleCreateFromScratch = useCallback(() => {
    const emptyCV = {
      personalInfo: { fullName: '', title: '', email: '', phone: '', location: '', links: [] },
      summary: '',
      experience: [],
      education: [],
      skills: { technical: [], soft: [], tools: [] },
      languages: [],
      certifications: [],
      projects: [],
      settings: { dateFormat: 'month-year' as const }
    }
    const newId = addCV(emptyCV, t.cvList.newCVDefault, template)
    loadCV(newId, emptyCV)
    previousCVIdRef.current = newId
    completeOnboarding()
    setShowWelcome(false)
  }, [addCV, loadCV, template, completeOnboarding])

  const handleLastCVDeleted = useCallback(() => {
    setShowWelcome(true)
  }, [])

  // Show welcome screen for new users
  if (showWelcome) {
    return (
      <>
        <WelcomeScreen
          onCreateFromScratch={handleCreateFromScratch}
          onImportPdf={() => setIsImportModalOpen(true)}
        />
        <ImportModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          onImportComplete={handleImportComplete}
        />
      </>
    )
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Desktop Layout */}
      <div className="hidden lg:flex flex-1 overflow-hidden">
        {/* Left Panel - CV List */}
        <CVListPanel
          isCollapsed={isPanelCollapsed}
          onToggleCollapse={() => setIsPanelCollapsed(!isPanelCollapsed)}
          onImportClick={() => setIsImportModalOpen(true)}
          onLastCVDeleted={handleLastCVDeleted}
        />

        {/* Center - Preview (main content) */}
        <div className="flex-1 min-w-0 overflow-hidden">
          <PreviewPane
            template={template}
            onTemplateChange={setTemplate}
          />
        </div>

        {/* Right Panel - Form */}
        <div className="w-[400px] h-full flex-shrink-0 flex flex-col border-l border-gray-200 bg-white">
          <CVFormSidebar />
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden flex flex-col flex-1 min-h-0 pb-[calc(56px+env(safe-area-inset-bottom))]">
        {/* Mobile Content */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {mobileTab === 'edit' && (
            <div className="h-full min-h-0 bg-white overflow-hidden">
              <CVFormSidebar />
            </div>
          )}
          {mobileTab === 'cvs' && (
            <div className="h-full min-h-0 bg-gray-50 overflow-hidden">
              <CVListPanel
                isCollapsed={false}
                onToggleCollapse={() => {}}
                onImportClick={() => setIsImportModalOpen(true)}
                onLastCVDeleted={handleLastCVDeleted}
              />
            </div>
          )}
          {mobileTab === 'preview' && (
            <PreviewPane
              template={template}
              onTemplateChange={setTemplate}
              isMobile
            />
          )}
        </div>

        {/* Mobile Bottom Navigation Bar */}
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 flex items-stretch pb-[env(safe-area-inset-bottom)]">
          {/* Editar */}
          <button
            onClick={() => setMobileTab('edit')}
            className={`flex-1 flex flex-col items-center justify-center py-2 min-h-[56px] transition-colors ${
              mobileTab === 'edit'
                ? 'text-primary'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <svg
              className="w-6 h-6 mb-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
            <span className="text-[10px] font-medium">{t.nav.edit}</span>
          </button>

          {/* Mis CVs */}
          <button
            onClick={() => setMobileTab('cvs')}
            className={`flex-1 flex flex-col items-center justify-center py-2 min-h-[56px] transition-colors relative ${
              mobileTab === 'cvs'
                ? 'text-primary'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <svg
              className="w-6 h-6 mb-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
            <span className="text-[10px] font-medium">{t.nav.myCVs}</span>
            {cvs.length > 1 && (
              <span className="absolute top-1 right-1/4 bg-primary text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center">
                {cvs.length}
              </span>
            )}
          </button>

          {/* Vista previa */}
          <button
            onClick={() => setMobileTab('preview')}
            className={`flex-1 flex flex-col items-center justify-center py-2 min-h-[56px] transition-colors ${
              mobileTab === 'preview'
                ? 'text-primary'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <svg
              className="w-6 h-6 mb-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
            <span className="text-[10px] font-medium">{t.nav.preview}</span>
          </button>

          {/* Importar */}
          <button
            onClick={handleMobileImportClick}
            className="flex-1 flex flex-col items-center justify-center py-2 min-h-[56px] text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg
              className="w-6 h-6 mb-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <span className="text-[10px] font-medium">{t.nav.import}</span>
          </button>
        </div>
      </div>

      {/* Import Modal */}
      <ImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImportComplete={handleImportComplete}
      />

      {/* Shortcuts Modal */}
      <ShortcutsModal
        isOpen={showShortcuts}
        onClose={() => setShowShortcuts(false)}
      />

      {/* Auth Modal (CV limit gate) */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialView="register"
        title={t.cvLimit.title}
        description={t.cvLimit.description}
      />

      {/* Download Modal (Ctrl+P shortcut) */}
      <DownloadModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={executeDownload}
      />
    </div>
  )
}
